import { ChevronDownIcon, ChevronUpIcon, ChatIcon, InfoIcon, LoadSpinner, MailIcon, NotificationIcon, PhoneIcon } from '@calizahq/react-components-v2'
import { EmailQueries } from '@sales-app/graphql/queries/email.queries'
import { VoiceAiSummaryQueries } from '@sales-app/graphql/queries/voiceAiSummary.queries'
import { ActivityFeed, ActivityType, Direction } from '@sales-app/types/ActivityFeed'
import { Email } from '@sales-app/types/Email'
import clsx from 'clsx'
import { formatInTimeZone } from 'date-fns-tz'
import parse from 'html-react-parser'
import React, { FC, useState } from 'react'

import { graphQLClient } from '@/hooks/useGraphql'
import { useToggle } from '@/hooks'

import { Attachments } from './Attachments'

type Props = {
  activity: ActivityFeed
  bookablePersonName: string
}

export const CommunicationLog: FC<Props> = ({ activity, bookablePersonName }) => {
  const [emailContent, setEmailContent] = useState<string | true>()
  const [aiSummary, setAiSummary] = useState<string | 'loading' | 'error' | null>(null)
  const { isShown: aiSummaryExpanded, toggle: toggleAiSummary } = useToggle()
  const [emailError, setEmailError] = useState<string>()
  const containerStyled = 'mb-2 flex w-4/5 flex-col md:mb-4 md:w-2/3'
  const textStyled = 'flex w-full flex-col rounded-b-xl p-4 text-p-sm bg-emphasis md:text-base'
  const eventStyled = 'underline leading-140 mr-2 text-p-sm'

  const nonEventActivityTypes: string[] = Object.values(ActivityType)

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const formatedTime = activity.datetime
    ? formatInTimeZone(activity.datetime, timezone, 'MMM dd, yyyy hh:mm aa (z)')
    : ''
  const isNonEventType = nonEventActivityTypes.includes(activity.activityType)

  const handleToggleAiSummary = async (externalCallId: string) => {
    toggleAiSummary()
    if (!aiSummary) {
      setAiSummary('loading')
      try {
        const res: VoiceAiSummaryQueries.GET_VOICE_AI_RECAP_SUMMARY.Response = await graphQLClient.request(
          VoiceAiSummaryQueries.getVoiceAiRecapSummary(),
          { externalCallId },
        )
        setAiSummary(res.voiceAiRecapSummary?.recapSummary ?? 'error')
      } catch {
        setAiSummary('error')
      }
    }
  }

  const handleShowEmail = async (id: number) => {
    setEmailContent(true)
    const resRespons: EmailQueries.GET_EMAIL.Response = await graphQLClient.request(EmailQueries.getEmail(), {
      emailId: id,
    })
    setEmailContent(undefined)
    if (resRespons.email) {
      const decodedEmail = getDecodedEmail(resRespons.email)
      if (decodedEmail) {
        setEmailContent(decodedEmail)
      } else {
        setEmailError('No body found for the email')
      }
    } else {
      setEmailError('Error fetching email')
    }
  }

  const getDecodedEmail = (email: Email) => {
    let content: null | string = null
    if (email.payload) {
      const { body, mimeType, parts } = email.payload
      if (parts && parts.length > 0) {
        parts.forEach((p) => {
          if (p.body && p.body.data && p.mimeType === 'text/html') {
            content = b64DecodeUnicode(p.body.data)
          }
        })
        if (!content) {
          parts.forEach((p) => {
            if (p.body && p.body.data && p.mimeType === 'multipart/related') {
              content = b64DecodeUnicode(p.body.data)
            }
          })
        }
      }
      if (!content) {
        if (body && body.data) {
          if (mimeType === 'text/html' || mimeType === 'multipart/related') {
            content = b64DecodeUnicode(body.data as string)
          }
        }
      }
    }
    return content
  }
  function b64DecodeUnicode(str) {
    return decodeURIComponent(
      Array.prototype.map
        .call(atob(str), function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join(''),
    )
  }

  return (
    <div className={clsx(containerStyled, activity.direction === Direction.Outbound && 'self-end')}>
      <div className={`mb-1 flex items-center ${activity.direction === Direction.Outbound ? 'justify-end' : ''} `}>
        {activity.activityType === ActivityType.SMS && <ChatIcon className="w-4" />}
        {activity.activityType === ActivityType.EMAIL && <MailIcon className="w-4" />}
        {activity.activityType === ActivityType.CALL && <PhoneIcon className="w-4" />}
        {activity.activityType === ActivityType.PUSH_NOTIFICATION && <NotificationIcon className="w-4" />}
        {!isNonEventType && <InfoIcon className="w-4" />}
        {activity.direction === Direction.Inbound && <p className="  ml-2 capitalize">{bookablePersonName || ''}</p>}
        {activity.direction === Direction.Outbound && <p className=" ml-2 capitalize">{activity.agentName || ''}</p>}
      </div>
      <div className={`flex items-center ${activity.direction === Direction.Outbound ? 'justify-end' : ''} `}>
        <p className="text-p-xs  text-tertiary">{activity.activityType} &middot; </p>
        <p className=" ml-1  text-p-xs  text-tertiary">{formatedTime}</p>
      </div>
      {activity.originatingParty && (
        <div className={`flex items-center ${activity.direction === Direction.Outbound ? 'justify-end' : ''} `}>
          <p className="text-p-sm text-tertiary">
            from: {activity.originatingParty} {activity.destinationParty && ` to: ${activity.destinationParty}`}
          </p>
        </div>
      )}
      {activity.content && isNonEventType && (
        <div
          className={clsx(
            textStyled,
            activity.direction === Direction.Inbound ? 'rounded-tr-xl' : 'rounded-tl-xl',
            activity.direction === Direction.Outbound ? 'bg-action-secondary' : 'bg-green-100',
          )}
          style={{ overflowWrap: 'break-word' }}
        >
          {activity.activityType === ActivityType.CALL && (
            <div className="flex flex-col gap-1">
              <a className="cursor-pointer underline" href={activity.content} rel="noreferrer" target="_blank">
                Open audio recording
              </a>
              {activity.externalSource === 'dialpad' && activity.externalId && (
                <div className="mt-1">
                  <div
                    className="flex cursor-pointer items-center gap-1"
                    onClick={() => handleToggleAiSummary(activity.externalId)}
                    role="button"
                  >
                    {aiSummaryExpanded ? (
                      <ChevronUpIcon className="icon-primary" width={14} />
                    ) : (
                      <ChevronDownIcon className="icon-primary" width={14} />
                    )}
                    <p className="text-p-xs text-tertiary">AI Summary</p>
                  </div>
                  <div
                    className={clsx(
                      'overflow-hidden transition-all duration-300 ease-in-out',
                      aiSummaryExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
                    )}
                  >
                    <div className="mt-2 border-t border-white/40 pt-2">
                      {aiSummary === 'loading' && (
                        <LoadSpinner className="icon-secondary" isCurrentColor={true} size={1} />
                      )}
                      {aiSummary && aiSummary !== 'loading' && aiSummary !== 'error' && (
                        <p className="whitespace-pre-wrap text-p-sm leading-relaxed">{aiSummary}</p>
                      )}
                      {aiSummary === 'error' && (
                        <p className="text-p-sm text-semantic-error">No AI summary available for this call.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {activity.activityType === ActivityType.SMS && <p>{activity.content}</p>}
          {activity.activityType === ActivityType.PUSH_NOTIFICATION && <p>{activity.content}</p>}
          {activity.activityType === ActivityType.COMMENT && <p>{activity.content}</p>}

          {activity.activityType === ActivityType.EMAIL && (
            <>
              <p className="mb-2">Subject:{activity.content}</p>
              {!emailContent && !emailError && (
                <button className=" w-fit-content underline" onClick={() => handleShowEmail(activity.activityId)}>
                  Show Content
                </button>
              )}
              {emailContent && typeof emailContent == 'boolean' && (
                <LoadSpinner className="h-6 icon-secondary" isCurrentColor={true} size={1} />
              )}
              {emailContent && typeof emailContent == 'string' && (
                <div className=" border-t-2 border-white pt-2">{parse(emailContent)}</div>
              )}
              {emailError && <div className=" border-t-2 border-white pt-2 text-semantic-error">{emailError}</div>}
            </>
          )}

          {activity.attachments && <Attachments attachments={activity.attachments} />}
        </div>
      )}
      {!isNonEventType && (
        <div className="flex">
          {activity.displayCity && <span className={eventStyled}>City: {activity.displayCity}</span>}
          {activity.home && <span className={eventStyled}>Home: {activity.home}</span>}
          {activity.homeId && <span className={eventStyled}>Home ID: #{activity.homeId}</span>}
          {activity.reservationId && <span className={eventStyled}>Reservation ID: #{activity.reservationId}</span>}
        </div>
      )}
    </div>
  )
}
